/**
 * Per-user chart colors.
 *
 * Each user's color is PERSISTED on the user record (`User.color`) and assigned
 * server-side so it is stable across every chart, every device, and every
 * session — Lynn is the same blue everywhere. This module is the single source
 * of truth for the palette and the assignment rule, shared by the Worker API
 * (assign-on-create) and the client (display fallback for users with no color
 * yet, e.g. the synthetic `unknown` bucket).
 *
 * Keep {@link USER_COLORS} in sync with the backfill palette in
 * migrations/166_users_color.sql.
 */

/**
 * Shared palette. Assignment prefers the first UNUSED entry, so order matters.
 * Sized (24) to exceed a typical org so every active user gets a distinct color;
 * past this, reuse is unavoidable (and perceptual distinctness degrades anyway).
 */
export const USER_COLORS = [
	'#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336',
	'#00BCD4', '#795548', '#607D8B', '#E91E63', '#3F51B5',
	'#009688', '#CDDC39', '#FF5722', '#673AB7', '#8BC34A',
	'#FFC107', '#03A9F4', '#E040FB', '#1DE9B6', '#827717',
	'#D500F9', '#00C853', '#6200EA', '#C2185B',
];

/** Color for users that are de-emphasized (e.g. not the selected filter). */
export const GRAYED_USER_COLOR = '#D0D0D0';

/** Deterministic djb2-style rolling hash → non-negative integer. */
const hashUserId = (userId: string): number => {
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		// hash * 31 + charCode, kept in 32-bit int range
		hash = (hash * 31 + userId.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
};

/**
 * Pick the next color for a brand-new user, guaranteeing no overlap with the
 * colors already in use — used server-side at create time.
 *
 * Returns the first palette entry not present in `usedColors`. Only when the
 * palette is exhausted (more users than colors) does it fall back to a
 * deterministic hash of the seed id, at which point reuse is unavoidable.
 *
 * @param usedColors colors already assigned to other (live) users
 * @param seedId stable id used for the deterministic fallback
 */
export const pickUnusedColor = (usedColors: Iterable<string>, seedId: string): string => {
	const used = new Set(usedColors);
	const free = USER_COLORS.find(c => !used.has(c));
	return free ?? USER_COLORS[hashUserId(seedId || 'unknown') % USER_COLORS.length];
};

/**
 * Deterministic display-only color for a user id. Used as a client fallback
 * when a record has no persisted color yet (or for the `unknown` bucket). May
 * collide; the authoritative, collision-free color lives on the user record.
 *
 * @param userId stable user identifier
 */
export const getUserColor = (userId: string): string => {
	return USER_COLORS[hashUserId(userId || 'unknown') % USER_COLORS.length];
};

/** Starting point for the text-darkening search below. */
const TEXT_DARKEN_FACTOR = 0.62;

/** WCAG AA for body text. These labels render at 12px on a light surface. */
const TEXT_CONTRAST_TARGET = 4.5;

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** WCAG relative luminance of a #RRGGBB colour. */
const relativeLuminance = (hex: string): number => {
	const channels = [1, 3, 5].map((offset) => {
		const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	});
	return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

/** Contrast ratio against white, which is what these labels sit on. */
const contrastOnWhite = (hex: string): number => 1.05 / (relativeLuminance(hex) + 0.05);

/**
 * Darken a hex colour by scaling each channel toward black.
 *
 * Scaling rather than blending with a grey keeps the HUE and the relative
 * channel mix intact, so a darkened colour still reads as the same colour —
 * which is the whole point when the colour IS the identifier.
 *
 * Anything that isn't a #RGB / #RRGGBB literal comes back untouched: callers
 * pass a user-editable value, and a malformed one should degrade to "no change"
 * rather than to black.
 */
export const darkenColor = (hex: string, factor = TEXT_DARKEN_FACTOR): string => {
	const match = (hex ?? '').trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
	if (!match) return hex;
	// Expand the shorthand form so one parser handles both.
	const full = match[1].length === 3 ? match[1].split('').map((c) => c + c).join('') : match[1];
	const channel = (offset: number): string => {
		const value = Math.round(parseInt(full.slice(offset, offset + 2), 16) * factor);
		return Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0');
	};
	return `#${channel(0)}${channel(2)}${channel(4)}`;
};

/**
 * The colour to render a user's NAME in.
 *
 * {@link USER_COLORS} is tuned for filled row highlights and chart series, where
 * the colour sits BEHIND text. Used AS text on a light surface those mid-tones
 * land around 2–3:1 against white, under the 4.5:1 needed to read comfortably.
 *
 * Darkened until it CLEARS the contrast target rather than by a fixed amount:
 * the palette spans lime (#CDDC39) to indigo (#3F51B5), and one factor big
 * enough to rescue the lime turns the indigo to mud. A flat 0.62 left the
 * brightest entries at 3.85:1 — close enough to look deliberate and still fail.
 *
 * Hue is preserved throughout (see {@link darkenColor}), so each PM stays
 * recognisably "the blue one".
 *
 * @param userId stable user id, for the deterministic fallback
 * @param persisted the authoritative `users.color`, when the record carries one
 */
export const getUserTextColor = (userId: string, persisted?: string | null): string => {
	const base = (persisted || getUserColor(userId)).trim();
	// A malformed persisted value can't be measured; hand it back rather than
	// darkening something that isn't a colour.
	if (!HEX_PATTERN.test(base)) return base;

	let candidate = darkenColor(base, TEXT_DARKEN_FACTOR);
	// Bounded, so a pathological input can never spin: 12 steps of 4% reaches
	// ~0.14 of the original, darker than anything in the palette needs.
	for (let step = 0; step < 12 && contrastOnWhite(candidate) < TEXT_CONTRAST_TARGET; step++) {
		candidate = darkenColor(base, TEXT_DARKEN_FACTOR - (step + 1) * 0.04);
	}
	return candidate;
};
